import { Icon } from '@iconify/react';
import { AlignLeft, FileText, FolderOpen, Globe, Plus, Youtube, Lock } from 'lucide-react';
import { type LucideIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { UpgradePlanButton } from '@/Domain/components/UpgradePlanButton/UpgradePlanButton';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useArticleLimitCheck } from '@/Domain/hooks/KnowledgeBase/useArticleLimitCheck.hook';
import { useCreateArticle } from '@/Domain/hooks/KnowledgeBase/useCreateArticle.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

type CardConfig = {
  label: string;
  lucideIcon?: LucideIcon;
  iconifyIcon?: string;
  onClick: () => void;
  dataTestId: string;
  disabled?: boolean;
};

export const AddContentCards = () => {
  const { t } = useTranslation('knowledge_base');
  const { toggleModal } = useModalStore();
  const { handleCreateArticle, isPending } = useCreateArticle();
  const handleArticleLimitCheck = useArticleLimitCheck();
  const { getConfig, isFetched } = useSubscription();
  const { capture } = usePostHogAnalytics();
  const canAdd = getConfig('can_add_article') as boolean | undefined;
  const articlesLimit = getConfig('articles_limit') as number | null | undefined;
  const articlesUsed = getConfig('articles_used') as number | undefined;
  const atLimit = isFetched && canAdd === false;

  const wasAtLimit = useRef(false);
  useEffect(() => {
    if (atLimit && !wasAtLimit.current) {
      capture(PostHogEventEnum.KbLimitBannerViewed, {
        articles_used: articlesUsed,
        articles_limit: articlesLimit,
      });
    }
    wasAtLimit.current = atLimit;
  }, [atLimit, articlesUsed, articlesLimit, capture]);

  // Gate every add action through the limit check — the modal is the
  // friendly way to tell the user they're capped, vs the user clicking
  // through several modals only to be rejected at submit time.
  const gated = (action: () => void) => () => handleArticleLimitCheck(action);

  const handleCreateArticleClick = gated(() => {
    handleCreateArticle({ title: '', content: '', url: '', tags: [], itemsCount: 0 });
  });

  const cards: CardConfig[] = [
    {
      label: t('add_content.create_article'),
      lucideIcon: FileText,
      onClick: handleCreateArticleClick,
      dataTestId: 'add-content-article',
      disabled: isPending,
    },
    {
      label: t('add_content.create_paragraph'),
      lucideIcon: AlignLeft,
      onClick: gated(() => toggleModal(MODAL_NAMES.PARAGRAPH_SHEET)),
      dataTestId: 'add-content-paragraph',
    },
    {
      label: t('add_content.add_url'),
      lucideIcon: Globe,
      onClick: gated(() => toggleModal(MODAL_NAMES.UPLOAD_URL_MODAL_ID)),
      dataTestId: 'add-content-url',
    },
    {
      label: t('add_content.add_file'),
      lucideIcon: FolderOpen,
      onClick: gated(() => toggleModal(MODAL_NAMES.UPLOAD_MODAL_ID)),
      dataTestId: 'add-content-file',
    },
    {
      label: t('add_content.shopify'),
      iconifyIcon: 'simple-icons:shopify',
      onClick: gated(() => toggleModal(MODAL_NAMES.SHOPIFY_CONNECT_MODAL_ID)),
      dataTestId: 'add-content-shopify',
    },
    {
      label: t('add_content.intercom'),
      iconifyIcon: 'simple-icons:intercom',
      onClick: gated(() => toggleModal(MODAL_NAMES.INTERCOM_ARTICLES_MODAL_ID)),
      dataTestId: 'add-content-intercom',
    },
    {
      label: t('add_content.youtube'),
      lucideIcon: Youtube,
      onClick: gated(() => toggleModal(MODAL_NAMES.UPLOAD_YOUTUBE_MODAL_ID)),
      dataTestId: 'add-content-youtube',
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {atLimit && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-warning/20 bg-warning-muted px-4 py-2.5 text-sm text-warning"
          data-testid="kb-articles-at-limit-banner"
        >
          <div className="flex min-w-0 items-start gap-2 sm:items-center">
            <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning sm:mt-0" />
            {/* No `truncate` here — let the message wrap to a second line on
                mobile so the Upgrade button can keep its full label. */}
            <span className="min-w-0">
              {articlesLimit
                ? t('add_content.at_limit_with_count', { limit: articlesLimit.toLocaleString() })
                : t('add_content.at_limit')}
            </span>
          </div>
          <UpgradePlanButton size="xs" source="kb_add_content_banner" className="shrink-0" />
        </div>
      )}

      <div className="flex gap-3 overflow-x-auto pb-2 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border">
        {cards.map(
          ({ label, lucideIcon: LucideIcon, iconifyIcon, onClick, dataTestId, disabled }) => (
            <button
              key={dataTestId}
              data-testid={dataTestId}
              onClick={onClick}
              disabled={disabled}
              className={`flex h-[5.5rem] w-[7.5rem] shrink-0 cursor-pointer flex-col justify-between rounded-2xl border border-border bg-card p-3.5 text-left transition-colors hover:border-foreground-subtle hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 ${
                atLimit ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-center justify-between w-full">
                {LucideIcon ? (
                  <LucideIcon className="h-5 w-5 text-muted-foreground" />
                ) : iconifyIcon ? (
                  <Icon icon={iconifyIcon} className="h-5 w-5 text-muted-foreground" />
                ) : null}
                <Plus className="h-3.5 w-3.5 text-foreground-subtle" />
              </div>
              <span className="w-full truncate text-sm font-medium text-foreground">{label}</span>
            </button>
          ),
        )}
      </div>
    </div>
  );
};
